import { useState, useRef, useEffect, type CSSProperties } from 'react';

interface ColorPickerProps {
  value: string;
  onChange: (color: string) => void;
  label?: string;
}

/**
 * Color picker with native color input and hex text field
 */
export function ColorPicker({ value, onChange, label }: ColorPickerProps) {
  const [localValue, setLocalValue] = useState(value || '#cccccc');
  const inputRef = useRef<HTMLInputElement>(null);

  // Sync local value with prop
  useEffect(() => {
    if (value && value !== localValue) {
      setLocalValue(value);
    }
  }, [value]);

  const handleColorChange = (newColor: string) => {
    setLocalValue(newColor);
    onChange(newColor);
  };

  const handleTextChange = (text: string) => {
    setLocalValue(text);
    // Only propagate valid hex colors
    if (/^#[0-9A-Fa-f]{6}$/.test(text)) {
      onChange(text);
    }
  };

  const handleSwatchClick = () => {
    inputRef.current?.click();
  };

  const containerStyle: CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  };

  const labelStyle: CSSProperties = {
    fontSize: '14px',
    fontWeight: 500,
    color: 'var(--color-text-secondary)',
  };

  const pickerContainerStyle: CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  };

  const swatchContainerStyle: CSSProperties = {
    position: 'relative',
    cursor: 'pointer',
  };

  const swatchStyle: CSSProperties = {
    width: '48px',
    height: '48px',
    borderRadius: '8px',
    backgroundColor: localValue,
    border: '2px solid var(--color-border)',
    cursor: 'pointer',
  };

  const nativePickerStyle: CSSProperties = {
    position: 'absolute',
    opacity: 0,
    width: '48px',
    height: '48px',
    cursor: 'pointer',
    top: 0,
    left: 0,
  };

  const inputStyle: CSSProperties = {
    flex: 1,
    padding: '12px',
    borderRadius: '8px',
    border: '1px solid var(--color-border)',
    backgroundColor: 'var(--color-bg-secondary)',
    color: 'var(--color-text-primary)',
    fontSize: '14px',
    fontFamily: 'monospace',
    textTransform: 'uppercase',
  };

  return (
    <div style={containerStyle}>
      {label && <label style={labelStyle}>{label}</label>}
      <div style={pickerContainerStyle}>
        <div style={swatchContainerStyle} onClick={handleSwatchClick}>
          <input
            ref={inputRef}
            type="color"
            value={localValue}
            onChange={(e) => handleColorChange(e.target.value)}
            style={nativePickerStyle}
          />
          <div style={swatchStyle} />
        </div>
        <input
          type="text"
          value={localValue}
          onChange={(e) => handleTextChange(e.target.value)}
          placeholder="#000000"
          style={inputStyle}
          maxLength={7}
        />
      </div>
    </div>
  );
}
